import { useApolloClient, useQuery } from "@apollo/client";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Banner from "@/components/Banner";
import SegmentedButton from "@/components/SegmentedButton";
import SubSectionHeading from "@/components/SubSectionHeading";
import { DOMAIN, PERMISSION, useHasCapabilityBool } from "@/hooks/useCapabilities";
import { copyTextToClipboard } from "@/lib/copyToClipboard";
import { showErrorSnackbar, showSnackbar } from "@/lib/snackbarUtils";
import {
  NATIONAL_SECTION_LEGACY_ID,
  NATIONAL_SECTION_NAME,
  resolveNationalSectionId
} from "@/pages/people/membership-services/committees/constants";
import {
  assignmentRoleFilter,
  buildCommitteeMailtoHref,
  formatEmailAddressList,
  isMailtoTooLong,
  openMailtoHref,
  type MailtoField
} from "@/pages/people/membership-services/committees/_components/committeeMailto";
import { fetchCrmSectionsForGroupEmail } from "@/pages/people/membership-services/committees/_components/groupEmailCatalog";
import {
  GroupEmailActions,
  GroupEmailAddToField,
  GroupEmailCheckboxList,
  GroupEmailLoadingStatus,
  GroupEmailRecipientStatus
} from "@/pages/people/membership-services/committees/_components/groupEmailUi";
import { NATIONAL_SECTION_QUERY } from "@/pages/people/membership-services/committees/queries";
import {
  GOVERNANCE_TITLES_QUERY,
  type GovernanceTitlesQueryData,
  type GovernanceTitlesQueryVariables,
  type SectionGovernanceTargetType
} from "@/pages/sections/[sectionId]/operations/_components/sectionGovernanceQueries";
import {
  collectEmailsFromGovernanceRoles,
  fetchGovernanceEmailsForSections,
  type GovernanceEmailResult
} from "./governanceMailto";
import type { SectionGovernanceRoleEmailNode } from "../queries";

export type GovernanceScope = "national" | "other";

type SectionGovernanceEmailFormProps = {
  active: boolean;
};

const EMPTY_RESULT: Pick<GovernanceEmailResult, "emails" | "participantCount" | "excludedCount"> = {
  emails: [],
  participantCount: 0,
  excludedCount: 0
};

function governanceMailtoSubject(scope: GovernanceScope): string {
  return scope === "national" ? "National Governance" : "Section Governance";
}

export default function SectionGovernanceEmailForm({ active }: SectionGovernanceEmailFormProps) {
  const client = useApolloClient();
  const canViewPersonContact = useHasCapabilityBool({
    domain: DOMAIN.PEOPLE,
    permission: PERMISSION.viewPersonContact
  });

  const [governanceScope, setGovernanceScope] = useState<GovernanceScope>("national");
  const [sections, setSections] = useState<Array<{ id: string; name: string; legacyId?: string | null }>>([]);
  const [sectionsLoading, setSectionsLoading] = useState(false);
  const [sectionsError, setSectionsError] = useState(false);
  const [sectionsTruncated, setSectionsTruncated] = useState(false);
  const [selectedSectionIds, setSelectedSectionIds] = useState<Set<string>>(new Set());
  const [selectedTitleIds, setSelectedTitleIds] = useState<Set<string>>(new Set());
  const [field, setField] = useState<MailtoField>("bcc");
  const [roleNodes, setRoleNodes] = useState<SectionGovernanceRoleEmailNode[]>([]);
  const [recipientsLoading, setRecipientsLoading] = useState(false);
  const [recipientsTruncated, setRecipientsTruncated] = useState(false);
  const [copying, setCopying] = useState(false);
  const roleCacheRef = useRef(new Map<string, { nodes: SectionGovernanceRoleEmailNode[]; truncated: boolean }>());

  const titleTargetType: SectionGovernanceTargetType = governanceScope === "national" ? "NATIONAL" : "SECTION";

  const {
    data: nationalSectionData,
    loading: nationalSectionLoading,
    error: nationalSectionError
  } = useQuery(NATIONAL_SECTION_QUERY, {
    variables: { input: { search: NATIONAL_SECTION_NAME } },
    skip: !active || governanceScope !== "national"
  });
  const nationalSectionId = useMemo(
    () =>
      governanceScope === "national" ? resolveNationalSectionId(nationalSectionData?.crmSections?.nodes) : undefined,
    [governanceScope, nationalSectionData?.crmSections?.nodes]
  );
  const nationalSectionMissing = Boolean(
    governanceScope === "national" && !nationalSectionLoading && !nationalSectionId && !nationalSectionError
  );

  const {
    data: titlesData,
    loading: titlesLoading,
    error: titlesError
  } = useQuery<GovernanceTitlesQueryData, GovernanceTitlesQueryVariables>(GOVERNANCE_TITLES_QUERY, {
    variables: { targetType: titleTargetType },
    skip: !active
  });
  const positionTitles = titlesData?.governanceTitles ?? [];
  const titleIdsKey = positionTitles.map((title) => title.id).join(",");

  const otherSections = useMemo(
    () =>
      sections.filter(
        (section) => section.legacyId !== NATIONAL_SECTION_LEGACY_ID && section.name !== NATIONAL_SECTION_NAME
      ),
    [sections]
  );

  const selectedSectionIdsKey = useMemo(() => Array.from(selectedSectionIds).sort().join(","), [selectedSectionIds]);

  const sectionIdsForFetch = useMemo(() => {
    if (governanceScope === "national") {
      return nationalSectionId ? [nationalSectionId] : [];
    }
    return selectedSectionIdsKey ? selectedSectionIdsKey.split(",") : [];
  }, [governanceScope, nationalSectionId, selectedSectionIdsKey]);

  const recipientResult = useMemo(() => {
    if (titlesLoading || titlesError || (titleIdsKey && selectedTitleIds.size === 0)) {
      return EMPTY_RESULT;
    }
    return collectEmailsFromGovernanceRoles(
      roleNodes,
      assignmentRoleFilter({
        catalogTitleIds: titleIdsKey ? titleIdsKey.split(",") : [],
        selectedTitleIds
      })
    );
  }, [roleNodes, selectedTitleIds, titleIdsKey, titlesError, titlesLoading]);

  useEffect(() => {
    if (!active) {
      setGovernanceScope("national");
      setField("bcc");
      setSelectedSectionIds(new Set());
      setSelectedTitleIds(new Set());
      setRoleNodes([]);
      setRecipientsTruncated(false);
      setSectionsTruncated(false);
      roleCacheRef.current = new Map();
      return;
    }
  }, [active]);

  useEffect(() => {
    if (!active || governanceScope !== "other") {
      if (governanceScope !== "other") {
        setSections([]);
        setSectionsLoading(false);
        setSectionsError(false);
        setSectionsTruncated(false);
      }
      return;
    }

    let cancelled = false;
    setSectionsLoading(true);
    setSectionsError(false);
    setSectionsTruncated(false);

    void fetchCrmSectionsForGroupEmail(client)
      .then(({ sections: nextSections, truncated }) => {
        if (cancelled) return;
        setSections(nextSections);
        setSectionsTruncated(truncated);
      })
      .catch(() => {
        if (cancelled) return;
        setSections([]);
        setSectionsError(true);
      })
      .finally(() => {
        if (cancelled) return;
        setSectionsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [active, client, governanceScope]);

  useEffect(() => {
    setSelectedTitleIds(new Set());
    roleCacheRef.current = new Map();
    setRoleNodes([]);
    setRecipientsTruncated(false);
    if (governanceScope === "other") {
      setSelectedSectionIds(new Set());
    }
  }, [governanceScope]);

  const sectionIdsForFetchKey = sectionIdsForFetch.join(",");
  const selectedTitleIdsKey = useMemo(() => Array.from(selectedTitleIds).sort().join(","), [selectedTitleIds]);
  const positionsReadyForFetch = !titlesLoading && (!titleIdsKey || selectedTitleIds.size > 0);

  useEffect(() => {
    if (!active || !sectionIdsForFetchKey || !canViewPersonContact || !positionsReadyForFetch) {
      setRoleNodes([]);
      setRecipientsLoading(false);
      setRecipientsTruncated(false);
      return;
    }

    const selectedIds = sectionIdsForFetchKey.split(",");
    for (const cachedId of Array.from(roleCacheRef.current.keys())) {
      if (!selectedIds.includes(cachedId)) {
        roleCacheRef.current.delete(cachedId);
      }
    }

    const applyCache = () => {
      const nodes = selectedIds.flatMap((id) => roleCacheRef.current.get(id)?.nodes ?? []);
      const truncated = selectedIds.some((id) => roleCacheRef.current.get(id)?.truncated === true);
      setRoleNodes(nodes);
      setRecipientsTruncated(truncated);
    };

    const missingIds = selectedIds.filter((id) => !roleCacheRef.current.has(id));
    if (missingIds.length === 0) {
      applyCache();
      setRecipientsLoading(false);
      return;
    }

    let cancelled = false;
    setRecipientsLoading(true);

    void fetchGovernanceEmailsForSections(client, { sectionIds: missingIds })
      .then((nextResult) => {
        if (cancelled) return;
        for (const [sectionId, result] of Object.entries(nextResult.bySection)) {
          roleCacheRef.current.set(sectionId, result);
        }
        applyCache();
        const incomplete =
          nextResult.truncated ||
          nextResult.failedSectionIds.length > 0 ||
          selectedIds.some((id) => roleCacheRef.current.get(id)?.truncated === true);
        setRecipientsTruncated(incomplete);
        if (incomplete) {
          showErrorSnackbar("Not all email addresses loaded", {
            subtitle: "You can still copy or open email with the addresses that loaded."
          });
        }
      })
      .catch(() => {
        if (cancelled) return;
        applyCache();
        if (roleCacheRef.current.size === 0) {
          setRecipientsTruncated(false);
          showErrorSnackbar("Email addresses not loaded", { subtitle: "Please try again later." });
          return;
        }
        setRecipientsTruncated(true);
        showErrorSnackbar("Not all email addresses loaded", {
          subtitle: "You can still copy or open email with the addresses that loaded."
        });
      })
      .finally(() => {
        if (cancelled) return;
        setRecipientsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [active, canViewPersonContact, client, positionsReadyForFetch, sectionIdsForFetchKey, selectedTitleIdsKey]);

  const selectedAvailableCount = otherSections.filter((section) => selectedSectionIds.has(section.id)).length;
  const allOtherSectionIds = otherSections.map((section) => section.id);
  const areAllSectionsSelected =
    allOtherSectionIds.length > 0 &&
    allOtherSectionIds.every((id) => selectedSectionIds.has(id)) &&
    selectedSectionIds.size === allOtherSectionIds.length;
  const areAllTitlesSelected =
    positionTitles.length > 0 && positionTitles.every((title) => selectedTitleIds.has(title.id));
  const hasRolesSelected = positionTitles.length === 0 || selectedTitleIds.size > 0;
  const hasPrimarySelection = governanceScope === "national" ? Boolean(nationalSectionId) : selectedSectionIds.size > 0;
  const hasEmails = recipientResult.emails.length > 0;
  const actionsDisabled =
    copying ||
    recipientsLoading ||
    titlesLoading ||
    Boolean(titlesError) ||
    nationalSectionLoading ||
    !hasEmails ||
    !hasPrimarySelection ||
    !hasRolesSelected ||
    (governanceScope === "other" && (sectionsLoading || sectionsError));

  const toggleSection = (sectionId: string) => {
    setSelectedSectionIds((previous) => {
      const next = new Set(previous);
      if (next.has(sectionId)) {
        next.delete(sectionId);
      } else {
        next.add(sectionId);
      }
      return next;
    });
  };

  const handleSelectAllSections = () => {
    if (otherSections.length === 0) return;
    if (areAllSectionsSelected) {
      setSelectedSectionIds(new Set());
      return;
    }
    setSelectedSectionIds(new Set(allOtherSectionIds));
  };

  const toggleTitle = (titleId: string) => {
    setSelectedTitleIds((previous) => {
      const next = new Set(previous);
      if (next.has(titleId)) {
        next.delete(titleId);
      } else {
        next.add(titleId);
      }
      return next;
    });
  };

  const handleSelectAllTitles = () => {
    if (positionTitles.length === 0) return;
    setSelectedTitleIds(areAllTitlesSelected ? new Set() : new Set(positionTitles.map((title) => title.id)));
  };

  const copyAddresses = useCallback(async () => {
    if (recipientResult.emails.length === 0) return false;
    try {
      await copyTextToClipboard(formatEmailAddressList(recipientResult.emails));
      return true;
    } catch {
      showErrorSnackbar("Email addresses not copied", { subtitle: "Please try again later." });
      return false;
    }
  }, [recipientResult.emails]);

  const handleCopyAddresses = useCallback(async () => {
    setCopying(true);
    try {
      const copied = await copyAddresses();
      if (copied) {
        showSnackbar("Email addresses copied");
      }
    } finally {
      setCopying(false);
    }
  }, [copyAddresses]);

  const handleOpenEmail = useCallback(async () => {
    if (recipientResult.emails.length === 0) return;
    const href = buildCommitteeMailtoHref({
      emails: recipientResult.emails,
      field,
      subject: governanceMailtoSubject(governanceScope)
    });

    if (isMailtoTooLong(href)) {
      setCopying(true);
      try {
        const copied = await copyAddresses();
        if (copied) {
          showSnackbar("This list is too long to open in email. Addresses copied instead.");
        }
      } finally {
        setCopying(false);
      }
      return;
    }

    openMailtoHref(href);
  }, [copyAddresses, field, governanceScope, recipientResult.emails]);

  const sectionsHeader = sectionsLoading
    ? "Sections"
    : selectedAvailableCount === 0
      ? `Sections (${otherSections.length})`
      : `Sections (${selectedAvailableCount} of ${otherSections.length})`;

  const primaryList =
    governanceScope === "other" ? (
      sectionsLoading || sectionsError ? (
        <section className="flex flex-col gap-3" data-testid="group-email-sections-discovery">
          <SubSectionHeading>{sectionsHeader}</SubSectionHeading>
          {sectionsLoading && <GroupEmailLoadingStatus>Loading sections…</GroupEmailLoadingStatus>}
          {sectionsError && (
            <p className="text-body-medium text-sys-brand-error" role="alert">
              Unable to load sections. Try again later.
            </p>
          )}
        </section>
      ) : (
        <GroupEmailCheckboxList
          heading={sectionsHeader}
          items={otherSections.map((section) => ({
            id: section.id,
            label: section.name
          }))}
          selectedIds={selectedSectionIds}
          onToggle={toggleSection}
          onSelectAll={handleSelectAllSections}
          selectAllLabel={areAllSectionsSelected ? "Deselect all" : "Select all"}
          selectAllTestId="group-email-select-all-sections"
          selectAllAriaLabel={areAllSectionsSelected ? "Deselect all sections" : "Select all sections"}
          listTestId="group-email-sections-list"
          namePrefix="group-email-section"
        >
          {sectionsTruncated && (
            <Banner
              variant="warn"
              inline
              description="The section list is incomplete. Some sections may be missing."
              testId="group-email-governance-sections-truncated"
            />
          )}
          {otherSections.length === 0 && (
            <p className="text-body-medium text-sys-brand-on-surface-variant">No sections found.</p>
          )}
        </GroupEmailCheckboxList>
      )
    ) : null;

  const positionsList = (
    <GroupEmailCheckboxList
      heading="Positions"
      items={positionTitles.map((title) => ({ id: title.id, label: title.title }))}
      selectedIds={selectedTitleIds}
      onToggle={toggleTitle}
      onSelectAll={handleSelectAllTitles}
      selectAllLabel={areAllTitlesSelected ? "Deselect all" : "Select all"}
      selectAllTestId="group-email-select-all-positions"
      selectAllAriaLabel={areAllTitlesSelected ? "Deselect all positions" : "Select all positions"}
      listTestId="group-email-positions-list"
      namePrefix="group-email-position"
      footerSlot={<p className="text-body-medium text-sys-brand-on-surface-variant">Active roles only.</p>}
    >
      {titlesLoading && <GroupEmailLoadingStatus>Loading positions…</GroupEmailLoadingStatus>}
      {titlesError && (
        <p className="text-body-medium text-sys-brand-error" role="alert">
          Unable to load positions. Try again later.
        </p>
      )}
    </GroupEmailCheckboxList>
  );

  return (
    <div className="flex flex-col gap-6" data-testid="group-email-governance-form">
      <section className="flex flex-col gap-3">
        <SubSectionHeading>Scope</SubSectionHeading>
        <SegmentedButton
          fullWidth
          ariaLabel="Section governance scope"
          value={governanceScope}
          onChange={(value) => {
            if (value === "national" || value === "other") setGovernanceScope(value);
          }}
          options={[
            { label: "National Section", value: "national" },
            { label: "Other Sections", value: "other" }
          ]}
        />
        {governanceScope === "national" && nationalSectionLoading && (
          <GroupEmailLoadingStatus>Loading National Section…</GroupEmailLoadingStatus>
        )}
        {nationalSectionError && (
          <p className="text-body-medium text-sys-brand-error" role="alert">
            Unable to load National Section. Try again later.
          </p>
        )}
        {nationalSectionMissing && (
          <p className="text-body-medium text-sys-brand-error" role="alert">
            National Section was not found.
          </p>
        )}
        {governanceScope === "national" && nationalSectionId && (
          <p className="text-body-medium text-sys-brand-on-surface-variant">
            Recipients are National Section governance role holders.
          </p>
        )}
        {governanceScope === "other" && (
          <p className="text-body-medium text-sys-brand-on-surface-variant">
            Select one or more sections, then choose governance positions.
          </p>
        )}
      </section>

      {/* Filter slot spacer to match committees Core checkbox rhythm */}
      <div className="min-h-6" aria-hidden="true" />

      {primaryList ? (
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 md:items-start">
          {primaryList}
          {positionsList}
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-6 md:max-w-xl">{positionsList}</div>
      )}

      <GroupEmailRecipientStatus
        visible={hasPrimarySelection && hasRolesSelected && !titlesError && !nationalSectionError}
        loading={recipientsLoading || titlesLoading || nationalSectionLoading}
        emailCount={recipientResult.emails.length}
        excludedCount={recipientResult.excludedCount}
        truncated={recipientsTruncated}
      />
      <GroupEmailAddToField
        field={field}
        onChange={setField}
        emailCount={recipientResult.emails.length}
        namePrefix="group-email-governance-field"
      />
      <GroupEmailActions
        onCopy={() => void handleCopyAddresses()}
        onOpen={() => void handleOpenEmail()}
        disabled={actionsDisabled}
        copying={copying}
        copyTestId="group-email-governance-copy"
        openTestId="group-email-governance-open"
      />
    </div>
  );
}
